<template>
  <div class="work-responsibility-control">
    <div class="work-responsibility-control__meta">
      <span class="work-responsibility-control__label">Vem har bollen</span>
      <strong>{{ currentResponsibilityLabel }}</strong>
      <span>{{ currentResponsibilitySinceLabel }}</span>
    </div>

    <div class="work-responsibility-control__select">
      <AppSelect
        v-model="selectedResponsibility"
        :options="responsibilityOptions"
        :disabled="isSaving"
        placeholder="Välj ansvar"
        hide-details
        slim
      />
    </div>
    <AppButton type="button" color="green" small :disabled="!canSave" :loading="isSaving" @click="saveResponsibility">
      Spara
    </AppButton>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import { formatDateTimeValue, WORK_RESPONSIBILITY_PARTY_LABELS, WorkResponsibilityParty } from '@staff/shared'
import { AppButton, AppSelect, useNotifications } from '@staff/ui'

import { setDistributorWorkResponsibility } from '@/services/application'
import { useApplicationContext } from './application.context'

const { application } = useApplicationContext()
const refreshApplication = inject<() => Promise<void>>('refreshApplication')
const { notify } = useNotifications()

const selectedResponsibility = ref<WorkResponsibilityParty | null>(null)
const isSaving = ref(false)

const responsibilityOptions: { value: WorkResponsibilityParty; text: string }[] = Object.entries(
  WORK_RESPONSIBILITY_PARTY_LABELS,
).map(([value, text]) => ({
  value: value as WorkResponsibilityParty,
  text,
}))

const currentResponsibility = computed(() => application.value?.workResponsibilityParty ?? null)
const currentResponsibilityLabel = computed(() =>
  currentResponsibility.value ? WORK_RESPONSIBILITY_PARTY_LABELS[currentResponsibility.value] : 'Ej satt',
)
const currentResponsibilitySinceLabel = computed(() =>
  application.value?.workResponsibilitySince ? `Sedan ${formatSince(application.value.workResponsibilitySince)}` : '',
)
const canSave = computed(
  () =>
    Boolean(selectedResponsibility.value) &&
    selectedResponsibility.value !== currentResponsibility.value &&
    !isSaving.value,
)

watch(
  currentResponsibility,
  (value) => {
    selectedResponsibility.value = value
  },
  { immediate: true },
)

const saveResponsibility = async () => {
  if (
    !application.value?.id ||
    !selectedResponsibility.value ||
    selectedResponsibility.value === currentResponsibility.value
  ) {
    return
  }

  try {
    isSaving.value = true
    await setDistributorWorkResponsibility(application.value.id, selectedResponsibility.value)
    await refreshApplication?.()
    notify({ type: 'success', title: 'Bollen har flyttats' })
  } catch (err) {
    notify({
      type: 'error',
      title: err instanceof Error ? err.message : 'Kunde inte flytta bollen',
      dismissable: true,
    })
  } finally {
    isSaving.value = false
  }
}

const formatSince = (value: string) => {
  const formatted = formatDateTimeValue(value)
  return formatted === '-' ? '-' : formatted.replace(/:\d{2}$/, '')
}
</script>

<style scoped lang="scss">
.work-responsibility-control {
  display: grid;
  grid-template-columns: minmax(104px, auto) 128px auto;
  align-items: end;
  gap: 8px;
  max-width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--color-border-default);
  border-radius: 6px;
  background: rgb(255 255 255 / 0.62);
}

.work-responsibility-control__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: var(--color-fg-muted);
  font-size: 0.75rem;
  line-height: 1.2;
  text-align: left;
  white-space: nowrap;

  strong {
    color: var(--color-fg-default);
    font-size: 0.875rem;
  }
}

.work-responsibility-control__label {
  color: var(--color-fg-muted);
}

.work-responsibility-control__select {
  width: 128px;
}

@media (max-width: 767px) {
  .work-responsibility-control {
    grid-template-columns: 1fr auto;
    width: 100%;
  }

  .work-responsibility-control__select {
    grid-column: 1 / 2;
    width: 100%;
  }
}
</style>
